// permission.guard.ts
import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AccountService } from '../services/account.service';

// Interfaz para tipado de datos de ruta
export interface RoutePermissionData {
  permissions?: string[];
  requireAllPermissions?: boolean;
  allowedRoles?: number[];
}

@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate, CanActivateChild {

  constructor(
    private accountService: AccountService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.checkPermissions(route, state);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.checkPermissions(route, state);
  }

  private checkPermissions(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    // Verificar si el usuario está autenticado (delegamos al AuthGuard)
    const session = this.accountService.getSession();
    if (!session) {
      this.router.navigate(['/login']);
      return false;
    }

    // Obtener datos de configuración de permisos de la ruta
    const data = route.data as RoutePermissionData;

    // Si no hay configuración de permisos, permitir acceso
    if (!data.permissions && !data.allowedRoles) {
      return true;
    }

    // Verificar roles permitidos
    if (data.allowedRoles && data.allowedRoles.length > 0) {
      const token = this.accountService.getToken();
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const userRole = Number(payload.idRol); // JWT almacena idRol como string
          
          // Si el rol del usuario está en la lista de roles permitidos
          if (data.allowedRoles.includes(userRole)) {
            return true;
          }
        } catch (error) {
          console.error('Error al decodificar token:', error);
        }
      }
      
      // Si no cumple con los roles requeridos, redirigir
      this.router.navigate(['/unauthorized']);
      return false;
    }

    // Verificar permisos específicos
    if (data.permissions && data.permissions.length > 0) {
      const requireAll = data.requireAllPermissions !== false; // Por defecto, requiere todos
      
      const hasPermission = requireAll
        ? data.permissions.every(permission => this.accountService.hasPermission(permission))
        : data.permissions.some(permission => this.accountService.hasPermission(permission));

      if (!hasPermission) {
        this.router.navigate(['/unauthorized']);
        return false;
      }

      return true;
    }

    // Si es admin, permitir acceso a todo
    if (this.accountService.isAdmin()) {
      return true;
    }

    // Por defecto, no permitir acceso
    this.router.navigate(['/unauthorized']);
    return false;
  }
}
